/**
 * Conversión de tablas markdown (GFM) a nodos Slate.
 *
 * Se escribe a mano y sin dependencias: en el monorepo no hay ninguna librería de
 * markdown instalada y no queremos añadir una solo para esto.
 *
 * Formato reconocido (el de GitHub):
 *
 *   | Requisito | Detalle |
 *   | --------- | :-----: |
 *   | Edad      | 18      |
 *
 * La fila separadora es obligatoria: es lo que distingue una tabla de un párrafo
 * que casualmente lleva barras verticales.
 */

export type TableAlign = 'left' | 'center' | 'right'

export type SlateText = { text: string }

export type SlateTableCell = {
  type: 'table-cell'
  /** Celda de cabecera (primera fila): se pinta como <th>. */
  header?: boolean
  align?: TableAlign
  children: SlateText[]
}

export type SlateTableRow = {
  type: 'table-row'
  children: SlateTableCell[]
}

export type SlateTable = {
  type: 'table'
  children: SlateTableRow[]
}

/** Divide una fila markdown en celdas, respetando las barras escapadas (`\|`). */
function splitRow(line: string): string[] {
  const trimmed = line.trim().replace(/^\|/, '').replace(/\|$/, '')
  const cells: string[] = []
  let current = ''
  for (let i = 0; i < trimmed.length; i++) {
    const char = trimmed[i]
    if (char === '\\' && trimmed[i + 1] === '|') {
      current += '|'
      i++
      continue
    }
    if (char === '|') {
      cells.push(current.trim())
      current = ''
      continue
    }
    current += char
  }
  cells.push(current.trim())
  return cells
}

/** ¿Es la fila separadora? (`| --- | :---: |`) */
function isDelimiterRow(line: string): boolean {
  const trimmed = line.trim()
  if (!trimmed.includes('-')) return false
  const cells = splitRow(trimmed)
  if (cells.length === 0) return false
  return cells.every((cell) => /^:?-{1,}:?$/.test(cell))
}

/** Una línea solo puede ser fila de tabla si contiene al menos una barra. */
function looksLikeRow(line: string): boolean {
  return line.includes('|')
}

function alignOf(delimiter: string): TableAlign | undefined {
  const startsWithColon = delimiter.startsWith(':')
  const endsWithColon = delimiter.endsWith(':')
  if (startsWithColon && endsWithColon) return 'center'
  if (endsWithColon) return 'right'
  if (startsWithColon) return 'left'
  return undefined
}

function makeCell(text: string, header: boolean, align?: TableAlign): SlateTableCell {
  const cell: SlateTableCell = { type: 'table-cell', children: [{ text }] }
  if (header) cell.header = true
  if (align) cell.align = align
  return cell
}

/**
 * Convierte un bloque de líneas en una tabla Slate.
 * Devuelve null si no es una tabla markdown válida.
 */
export function parseMarkdownTable(text: string): SlateTable | null {
  const lines = text.split('\n').filter((line) => line.trim() !== '')
  if (lines.length < 2) return null
  if (!looksLikeRow(lines[0]) || !isDelimiterRow(lines[1])) return null

  const headers = splitRow(lines[0])
  const delimiters = splitRow(lines[1])
  // GFM exige que cabecera y separador tengan el mismo número de columnas.
  if (headers.length !== delimiters.length) return null

  const aligns = delimiters.map(alignOf)

  const headerRow: SlateTableRow = {
    type: 'table-row',
    children: headers.map((cell, i) => makeCell(cell, true, aligns[i])),
  }

  const bodyRows: SlateTableRow[] = lines.slice(2).map((line) => {
    const cells = splitRow(line)
    // Se normaliza al ancho de la cabecera: sobrantes fuera, faltantes en blanco.
    const normalized = Array.from(
      { length: headers.length },
      (_, i) => cells[i] ?? ''
    )
    return {
      type: 'table-row',
      children: normalized.map((cell, i) => makeCell(cell, false, aligns[i])),
    }
  })

  return { type: 'table', children: [headerRow, ...bodyRows] }
}

/** Nodo párrafo del editor (el tipo canónico es `p`, no `paragraph`). */
const paragraph = (text: string) => ({ type: 'p', children: [{ text }] })

/**
 * Convierte texto plano (típicamente pegado) en nodos Slate, transformando en
 * tablas los bloques que sean tablas markdown y dejando el resto como párrafos.
 * Devuelve null si no se encontró ninguna tabla, para que el editor siga con su
 * comportamiento normal de pegado.
 */
export function markdownToSlateNodes(text: string): any[] | null {
  const lines = text.split('\n')
  const nodes: any[] = []
  let buffer: string[] = []
  let foundTable = false

  const flushParagraphs = () => {
    buffer.forEach((line) => nodes.push(paragraph(line)))
    buffer = []
  }

  for (let i = 0; i < lines.length; i++) {
    const isTableStart =
      looksLikeRow(lines[i]) &&
      i + 1 < lines.length &&
      isDelimiterRow(lines[i + 1])

    if (!isTableStart) {
      buffer.push(lines[i])
      continue
    }

    // Consume la tabla entera: cabecera, separador y todas las filas seguidas.
    let end = i + 2
    while (end < lines.length && looksLikeRow(lines[end]) && lines[end].trim() !== '') {
      end++
    }

    const table = parseMarkdownTable(lines.slice(i, end).join('\n'))
    if (!table) {
      buffer.push(lines[i])
      continue
    }

    flushParagraphs()
    nodes.push(table)
    foundTable = true
    i = end - 1
  }

  if (!foundTable) return null
  flushParagraphs()
  return nodes
}
